import React from "react";
import axios from "axios";
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { Text, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_THREE, utils } from "../../utils/globalVariables";
import { changeValueOfArrayImmutable, safeValue, getPropInSubLevelRole, sqlDateToHumanDate } from "../../utils/globalFunctions";

import ModalResponse from "../../components/general/ModalResponse";
import PaperWrapper from "../../components/utilities/PaperWrapper";

import ComponentCustomer from "../trxDeliveryNota/ComponentCustomer";
import ComponentNomorJemput from "../trxDeliveryNota/ComponentNomorJemput";
import ComponentAlamat from "../trxDeliveryNota/ComponentAlamat";
import ComponentBarang from "../trxDeliveryNota/ComponentBarang";
import ComponentDeliveryUtils from "../trxDeliveryNota/ComponentDeliveryUtils";

const SegeraJemputNotaBasicData = ({
    stateData,
    fnRefreshIndex,
    fnCloseDrawer,
    fnSuccessSubmit,
    fnSuccessBatal
}) => {

    const mountedRef = React.useRef(true);

    const { URI1 } = useParams();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetData: 'deliveryJemputNotaBasicData-1',
    })

    const [data, setData] = React.useState({
        data: [],
        dataFlag: 'idle',
        dataInitLoading: false,

        customerId: '',

        barang: {
            dataBarangCategory: [],
        },

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    React.useEffect(() => {
        if (URI1) {

            setData(d => ({ ...d, dataInitLoading: true }))

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContext.syswebapp);
            formDT.append('flag_all', utils.flagAll);
            formDT.append('trx_id', URI1);
            formDT.append('current_step', stateData.utilsRef.currentStep);
            formDT.append('is_cancel', stateData.utilsRef.isCancel);

            axios({
                method: 'post',
                url: BE_API_THREE + '/data_init_delivery_penjemputan__by_penjemputan_id',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, dataInitLoading: false }));

                if (response.data.flag_status === 'isset') {
                    setData(d => ({
                        ...d,
                        data: response.data.data,
                        dataFlag: (Array.isArray(response.data.data) && (response.data.data.length > 0)) ? true : false,

                        barang: {
                            ...d.barang,
                            dataBarangCategory: response.data.data_barang_category
                        },

                        customerId: (Array.isArray(response.data.data) && (response.data.data.length > 0)) ? response.data.data[0]['customer_id'] : '',
                    }))
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorGetData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'no-data') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: true,
                            content: 'Data tidak ditemukan.',
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContext(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContext(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({
                    ...d,
                    dataInitLoading: false
                }))

                notifications.show({
                    id: notificationId.current.errorGetData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: true,
                })
            })
        }
    }, [handleSetLoginContext, loginContext, loginContext.syswebapp, URI1, stateData.utilsRef.currentStep, stateData.utilsRef.isCancel])

    return (
        <React.Fragment>
            {data.dataInitLoading &&
                <PaperWrapper stateMT="1rem">
                    <Text size='sm' color="dimmed">Loading Data..</Text>
                </PaperWrapper>
            }

            {!data.dataInitLoading && Array.isArray(data.data) && (data.data.length <= 0) &&
                <PaperWrapper stateMT="1rem">
                    <Text size='sm' color="dimmed">No Data</Text>
                </PaperWrapper>
            }

            {!data.dataInitLoading && Array.isArray(data.data) && (data.data.length > 0) && Object.keys(data.data).map((keyD, indexD) => {
                return (
                    <React.Fragment key={keyD}>
                        {data.data[keyD].is_cancel === '1' &&
                            <Alert
                                icon={<IconAlertCircle size="1rem" />}
                                title="Delivery Dibatalkan"
                                color="red"
                                mb='2rem'
                            >
                                <Text size='sm' fw='500'>Keterangan</Text>
                                <Text size='sm' mb='.5rem'>{safeValue(data.data[keyD].cancel_catatan, '-')}</Text>

                                <Text size='sm' fw='500'>Dibatalkan Oleh</Text>
                                <Text size='sm' mb='.5rem'>{safeValue(data.data[keyD].cancel_user_name, '-')} - {getPropInSubLevelRole(data.data[keyD].cancel_user_level, 'caption', '-')}</Text>

                                <Text size='sm' fw='500'>Tanggal Pembatalan</Text>
                                <Text size='sm' mb='.5rem'>{sqlDateToHumanDate({
                                    stateDate: data.data[keyD].cancel_datetime,
                                    stateWithTime: true,
                                    stateLang: 'id',
                                    stateIsShort: false,
                                    stateDay: false,
                                    stateDefaultValue: ' - ',
                                    stateDateType: 'day'
                                })}</Text>
                            </Alert>
                        }

                        <ComponentCustomer
                            stateData={data.data[keyD]}
                        />

                        <ComponentNomorJemput
                            stateData={data.data[keyD]}
                            utilsRef={{
                                currentStep: stateData.utilsRef.currentStep,
                                isCancel: stateData.utilsRef.isCancel
                            }}
                            fnCloseDrawer={fnCloseDrawer}
                            fnSuccessBatal={fnSuccessBatal}
                        />

                        <ComponentAlamat
                            stateData={data.data[keyD]}
                            stateKey={keyD}
                            utilsRef={{
                                currentStep: stateData.utilsRef.currentStep,
                                isCancel: stateData.utilsRef.isCancel
                            }}

                            fnCloseDrawer={fnCloseDrawer}
                            fnSubmitAlamatSelector={(stateKey, stateProp, stateVal) => {
                                setData(d => ({
                                    ...d,
                                    data: changeValueOfArrayImmutable(data.data, stateKey, stateProp, stateVal),
                                }))
                            }}

                            fnSubmitAlamatLain={(stateKey, stateProp, newVal) => {
                                setData(d => ({
                                    ...d,
                                    data: changeValueOfArrayImmutable(data.data, stateKey, stateProp, newVal),
                                }))
                            }}
                        />

                        <ComponentBarang
                            dataDataKey={data.data[keyD]}
                            dataCategoryBarang={data.barang.dataBarangCategory}
                            stateKey={keyD}
                            stateCustomerId={data.customerId}
                            utilsRef={{
                                currentStep: stateData.utilsRef.currentStep,
                                isCancel: stateData.utilsRef.isCancel
                            }}

                            fnCloseDrawer={fnCloseDrawer}

                            fnBarangPackage={(stateKey, stateProp, newVal) => {
                                setData(d => ({
                                    ...d,
                                    data: changeValueOfArrayImmutable(data.data, stateKey, stateProp, newVal),
                                }))
                            }}

                            fnBarang={(stateKey, stateProp, newVal) => {
                                setData(d => ({
                                    ...d,
                                    data: changeValueOfArrayImmutable(data.data, stateKey, stateProp, newVal),
                                }))
                            }}

                            fnBarangDelete={(stateKey, stateProp, newVal) => {
                                setData(d => ({
                                    ...d,
                                    data: changeValueOfArrayImmutable(data.data, stateKey, stateProp, newVal),
                                }))
                            }}
                        />

                        <ComponentDeliveryUtils
                            dataDataKey={data.data[keyD]}
                            stateKey={keyD}
                            utilsRef={{
                                currentStep: stateData.utilsRef.currentStep,
                                isCancel: stateData.utilsRef.isCancel
                            }}

                            fnCloseDrawer={fnCloseDrawer}
                            fnRefreshIndex={fnRefreshIndex}

                            fnDeliveryMan={(stateKey, object) => {
                                const newValue = data.data.map((obj, idx) => {
                                    if (Number(idx) === Number(stateKey)) {
                                        return {
                                            ...obj,
                                            delivery_man_id: object ? object.id : null,
                                            delivery_man_name: object ? object.name : null,
                                            delivery_man_gender: object ? object.gender : null,
                                            delivery_man_level: object ? object.level : null,
                                            delivery_man_sub_level: object ? object.subLevel : null,
                                            delivery_man_is_active: object ? object.isActive : null,
                                        };
                                    }

                                    return obj;
                                })

                                setData(d => ({ ...d, data: newValue }));
                            }}

                            fnWaktuPenjemputan={(stateKey, newVal) => {
                                const newValue = data.data.map((obj, idx) => {
                                    if (Number(idx) === Number(stateKey)) {
                                        return {
                                            ...obj,
                                            waktu_jemput: newVal,
                                        };
                                    }

                                    return obj;
                                })

                                setData(d => ({ ...d, data: newValue }));
                            }}

                            fnKeterangan={(stateKey, newVal) => {
                                const newValue = data.data.map((obj, idx) => {
                                    if (Number(idx) === Number(stateKey)) {
                                        return {
                                            ...obj,
                                            keterangan: newVal,
                                        };
                                    }

                                    return obj;
                                })

                                setData(d => ({ ...d, data: newValue }));
                            }}
                        />
                    </React.Fragment>
                )
            })}

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />
        </React.Fragment>
    )
}

export default React.memo(SegeraJemputNotaBasicData);